/**
 * @module app.cm
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { DocumentsService } from 'app/core/services/documents-service';
import { CaseService } from 'app/cm/services/case-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { localSort } from 'app/shared/lib/sorting';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { DownloadManager } from 'app/shared/lib/download/manager';
import { getDocumentIcon } from 'app/shared/lib/utilities';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

const FAXNOTEPOPUP_PAGEID: string = '5006';

@autoinject()
export class DocumentsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: IDocumentsConfig = {};

    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    protected hasDownloadDocuments: boolean = false;
    protected showNotesPopup: boolean = false;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;

    protected allDocuments: CM.CaseDocumentWithAttachmentModel[] = [];
    protected displayedDocuments: CM.CaseDocumentWithAttachmentModel[] = [];
    protected cases: ReadonlyArray<CM.Case> = [];

    protected sorting = new Sorting();
    protected defaultSorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 20;

    protected validate = validate;
    protected isValid: boolean = false;

    protected getDocumentIcon = getDocumentIcon;

    ////****************** Element References ***************************************////

    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $documentForm!: Element;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: DocumentsService,
        private readonly caseService: CaseService,
        private readonly transitional: TransitionalManager,
        private readonly downloadManager: DownloadManager,
        private readonly context: HouseholdCasePageContext,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();

        this.defaultSorting.entries = [
            {
                reference: 'attachment.member.description',
                direction: SortDirection.Ascending
            }
        ]
    }

    protected bind(): void {
        if(!this.householdCaseId || !this.config) { throw new Error('Bindable properties [household-case-id] or [config] were not set'); }
        this.service.configure(this.config);
        this.downloadManager.configureService(this.config);
        this.reload();
    }

    ////****************** Events ***************************************************////

    protected handleDownloadFile(event: MouseEvent, document: Core.Document): void {
        this.downloadManager.configureService(this.config);
        this.downloadManager.handleDownloadFile(event, document, false);
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // if no sorting entries specified, use default sorting
        localSort(this.allDocuments, sorting.entries.length > 0 ? this.sorting : this.defaultSorting);
        this.updateDisplayedDocuments();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$modal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        this.hasDownloadDocuments = this.accountContext.hasPermission('CM.CaseDocumentAttachment.Download');
        this.showNotesPopup = this.accountContext.hasRouteAccess(FAXNOTEPOPUP_PAGEID);
    }

    public async reload(): Promise<void> {
        this.displayedDocuments = [];
        this.allDocuments = [];
        await this.loadCasesByHouseholdCase();
    }

    protected getSourceIcon(code: string): string | null {
        if(!code) { return null; }
        code = code.toLowerCase();
        if(code.startsWith('upload')) { return 'ui:icons.upload'; }
        if(code.startsWith('fax')) { return 'ui:icons.fax'; }
        return null;
    }

    protected shouldView(document: Core.Document): void {
        this.downloadManager.shouldView(document);
    }

    protected openNotePopup(document: Core.Document): void {
        if(document.id && document.queue?.id) {
            this.transitional.openTransitional(null, FAXNOTEPOPUP_PAGEID, [document.id, document.queue.id]);
        }
    }

    // collect the documents from each case into one flattened collection
    private consolidateDocumentsData(): void {
        this.cases.forEach(memberCase => {
            if(memberCase.documents && memberCase.documents?.length > 0) {
                this.allDocuments = [...this.allDocuments, ...memberCase.documents]
            }
        });

        this.resultCount = this.allDocuments.length;
        localSort(this.allDocuments, this.defaultSorting);
        this.updateDisplayedDocuments();
    }

    // update displayed documents based on current page
    private updateDisplayedDocuments(): void {
        const currentPage = this.$pager?.page || 0;
        this.displayedDocuments = this.allDocuments.slice(currentPage * this.pageSize, currentPage * this.pageSize + this.pageSize);
    }

    protected addDocument(): void {
        if(!this.householdCaseId) { throw new Error('Missing required property: householdCaseId'); }
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadCasesByHouseholdCase(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        const includes: string[] = [
            'documents',
            'documents.category',
            'documents.flags',
            'documents.source',
            'documents.mediaType',
            'documents.attachment',
            'documents.attachment.case',
            'documents.attachment.member',
            'documents.attachment.metadata.created.byUser',
            'documents.metadata.created.byTask',
            'documents.metadata.created.byTask.attachedTo.type.designation',
        ];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, includes);
            this.cases = response.results;
            this.consolidateDocumentsData();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
